
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, BookOpen, UserCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Course, User as UserType, PendingAccount, loadAllUsers, loadPendingCourses, loadPendingAccounts } from "@/utils/adminUtils";

import AdminHeader from "@/components/admin/AdminHeader";
import { PendingAccountsTab } from "@/components/admin/PendingAccountsTab";
import PendingCoursesTab from "@/components/admin/PendingCoursesTab";
import UsersListTab from "@/components/admin/UsersListTab";
import { AdminUserManagement } from "@/components/admin/AdminUserManagement";

const Admin = () => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [pendingCourses, setPendingCourses] = useState<Course[]>([]);
  const [pendingAccounts, setPendingAccounts] = useState<PendingAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending-accounts");
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();

  const refreshAllData = async () => {
    console.log("Refreshing all admin data");
    setIsLoading(true);
    
    try {
      const allUsers = await loadAllUsers();
      setUsers(allUsers);

      const courses = await loadPendingCourses();
      console.log("Loaded pending courses:", courses);
      setPendingCourses(courses);
      
      const accounts = await loadPendingAccounts();
      setPendingAccounts(accounts);
      
      toast({
        title: "Data Refreshed",
        description: "All admin data has been refreshed from the database.",
      });
    } catch (error) {
      console.error("Error refreshing admin data:", error);
      toast({
        title: "Error",
        description: "Failed to load some admin data. Please try refreshing the page.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkAdminAccess = async () => {
      // Wait for auth loading to complete
      if (authLoading) return;
      
      if (!user) {
        toast({
          title: "Access Denied",
          description: "You must be logged in to access this page.",
          variant: "destructive",
        });
        navigate("/login");
        return;
      }

      // Check if user is admin - only use database field
      if (!user.isAdmin) {
        toast({
          title: "Access Denied",
          description: "You do not have admin privileges.",
          variant: "destructive",
        });
        
        // Redirect based on user type
        if (user.profileType === "mentor") {
          navigate("/mentor-dashboard");
        } else if (user.profileType === "mentee") {
          navigate("/mentee-dashboard");
        } else {
          navigate("/dashboard");
        }
        return;
      }
      
      // User has admin access, load admin data
      refreshAllData();
    };
    
    checkAdminAccess();
  }, [user, authLoading, navigate, toast]);

  const handleTabChange = async (value: string) => {
    setActiveTab(value);
    
    if (value === "users-list") {
      const allUsers = await loadAllUsers();
      setUsers(allUsers);
    } else if (value === "pending-accounts") {
      const accounts = await loadPendingAccounts();
      setPendingAccounts(accounts);
    } else if (value === "pending-courses") {
      const courses = await loadPendingCourses();
      setPendingCourses(courses);
    } else if (value === "admin-management") {
      const allUsers = await loadAllUsers();
      setUsers(allUsers);
    }
  };

  const handleCoursesUpdate = async (updatedCourses: Course[]) => {
    console.log("Updating pending courses in Admin:", updatedCourses.length);
    setPendingCourses(updatedCourses);
    
    console.log("Forcing reload of pending courses from database after update");
    try {
      const freshCourses = await loadPendingCourses();
      console.log("Fresh pending courses after update:", freshCourses);
      setPendingCourses(freshCourses);
      
      toast({
        title: "Success",
        description: "Course list has been updated successfully.",
      });
    } catch (error) {
      console.error("Error reloading courses after update:", error);
      toast({
        title: "Warning",
        description: "The course was updated but the list failed to refresh. Please refresh the page.",
        variant: "destructive",
      });
    }
  };

  // Show loading while auth is loading or while we're checking admin access
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <AdminHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />
      <main className="container mx-auto px-4 py-4 md:py-8">
        <Tabs defaultValue={activeTab} value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className={`w-full mb-4 md:mb-6 ${isMobile ? 'flex overflow-x-auto scrollbar-hide p-1 justify-start' : 'grid grid-cols-4'}`}>
            <TabsTrigger 
              value="pending-accounts" 
              className={`flex items-center ${isMobile ? 'flex-shrink-0 px-3 py-2 mx-1' : 'space-x-2'}`}
            >
              <UserCheck className="h-4 w-4" />
              {!isMobile && <span>Pending Accounts</span>}
              {isMobile && <span className="ml-1 text-xs">Pending</span>}
            </TabsTrigger>
            <TabsTrigger 
              value="pending-courses" 
              className={`flex items-center ${isMobile ? 'flex-shrink-0 px-3 py-2 mx-1' : 'space-x-2'}`}
            >
              <BookOpen className="h-4 w-4" />
              {!isMobile && <span>Pending Courses</span>}
              {isMobile && <span className="ml-1 text-xs">Courses</span>}
            </TabsTrigger>
            <TabsTrigger 
              value="users-list" 
              className={`flex items-center ${isMobile ? 'flex-shrink-0 px-3 py-2 mx-1' : 'space-x-2'}`}
            >
              <User className="h-4 w-4" />
              {!isMobile && <span>Users</span>}
              {isMobile && <span className="ml-1 text-xs">Users</span>}
            </TabsTrigger>
            <TabsTrigger 
              value="admin-management" 
              className={`flex items-center ${isMobile ? 'flex-shrink-0 px-3 py-2 mx-1' : 'space-x-2'}`}
            >
              <User className="h-4 w-4" />
              {!isMobile && <span>Admin Management</span>}
              {isMobile && <span className="ml-1 text-xs">Admin</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending-accounts">
            <PendingAccountsTab 
              accounts={pendingAccounts} 
              onAccountApproved={refreshAllData}
              onAccountRejected={refreshAllData}
            />
          </TabsContent>

          <TabsContent value="pending-courses">
            <PendingCoursesTab 
              pendingCourses={pendingCourses}
              onCoursesUpdate={handleCoursesUpdate}
            />
          </TabsContent>

          <TabsContent value="users-list">
            <UsersListTab 
              users={users}
              onUsersUpdate={setUsers}
            />
          </TabsContent>

          <TabsContent value="admin-management">
            <AdminUserManagement 
              users={users}
              onUsersUpdate={setUsers}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
